import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Problem, apiService, UserSolution, ReviewVerificationResult, VerifyReviewRequest } from '../services/api';
import { useAuth } from '../contexts/FirebaseAuthContext';
import { useUsage } from '../contexts/UsageContext';
import { VoiceTextInput } from '../components/VoiceTextInput';
import { ReviewVerificationResult as ReviewVerificationResultComponent } from '../components/ReviewVerificationResult';

interface VoiceRecording {
  id: string;
  transcription: string;
  audioBlob: Blob;
  mimeType: string;
  timestamp: number;
}

interface ReviewSession {
  sessionId: string;
  problems: Problem[];
  currentIndex: number;
  startTime: number;
  reviewRound: number;
  reviewedProblems: {
    [problemId: string]: {
      result: 'remember' | 'need_review';
      notes?: string;
      timestamp: number;
    };
  };
}

export const ReviewPage: React.FC = () => {
  console.log('🔍 ReviewPage component loaded');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { userProfile, isAuthenticated, isLoading: authLoading } = useAuth();
  const { openPaywall } = useUsage();
  
  console.log('🔍 ReviewPage state:', { isAuthenticated, authLoading });
  
  const [session, setSession] = useState<ReviewSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [sessionTime, setSessionTime] = useState(0);
  const [problemTime, setProblemTime] = useState(0);
  const [userNotes, setUserNotes] = useState('');
  const [userSolution, setUserSolution] = useState<UserSolution | null>(null);
  const [explanationInput, setExplanationInput] = useState('');
  const [verificationResult, setVerificationResult] = useState<ReviewVerificationResult | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [currentProblemVoiceRecording, setCurrentProblemVoiceRecording] = useState<VoiceRecording | null>(null);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  const roundNumber = parseInt(searchParams.get('round') || '1');
  const currentProblem = session?.problems[session.currentIndex];

  // Timer effects
  useEffect(() => {
    if (!session) return;

    const interval = setInterval(() => {
      const now = Date.now();
      setSessionTime(Math.floor((now - session.startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [session]);

  useEffect(() => {
    if (!currentProblem) return;

    setProblemTime(0);
    const startTime = Date.now();
    const interval = setInterval(() => {
      setProblemTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [currentProblem]);

  // Fetch user solution and voice recording when current problem changes
  useEffect(() => {
    if (!currentProblem) {
      setUserSolution(null);
      setCurrentProblemVoiceRecording(null);
      return;
    }

    const fetchUserSolution = async () => {
      try {
        console.log('Fetching user solution for problem:', currentProblem.id);
        const solution = await apiService.getUserSolutionForProblem(currentProblem.id);
        console.log('User solution:', solution);
        setUserSolution(solution);
        // Set the user notes from the solution, or empty if no solution
        setUserNotes(solution?.notes || '');
      } catch (error) {
        console.log('No user solution found for problem:', currentProblem.id);
        setUserSolution(null);
        setUserNotes('');
      }
    };

    // Load voice recording for this problem
    const loadVoiceRecording = () => {
      try {
        const savedRecording = localStorage.getItem(`voice_recording_${currentProblem.id}`);
        if (savedRecording) {
          const recordingData = JSON.parse(savedRecording);
          // Note: We can't restore the Blob from localStorage, so we'll just show the transcription
          // In a production app, we'd store the audio file on the server
          setCurrentProblemVoiceRecording({
            id: recordingData.id,
            transcription: recordingData.transcription,
            audioBlob: new Blob(), // Empty blob since we can't restore from localStorage
            mimeType: recordingData.mimeType,
            timestamp: recordingData.timestamp
          });
          console.log('Loaded voice recording for problem:', currentProblem.id);
        } else {
          setCurrentProblemVoiceRecording(null);
        }
      } catch (error) {
        console.error('Failed to load voice recording:', error);
        setCurrentProblemVoiceRecording(null);
      }
    };

    fetchUserSolution();
    loadVoiceRecording();
  }, [currentProblem]);

  // Initialize review session
  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) {
      console.log('Auth still loading, waiting...');
      return;
    }
    
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to home');
      navigate('/');
      return;
    }

    const initializeSession = async () => {
      try {
        setLoading(true);
        
        console.log('Fetching Meta problems for review...');
        
        let problemsToReview = [];
        
        if (roundNumber === 1) {
          // Round 1: All Meta problems (regardless of completion status)
          console.log('Round 1: Fetching all Meta problems...');
          // Get all problems first, then filter by Meta (use high limit to get all)
          const response = await apiService.getProblems({ company: 'Meta', limit: 200 });
          console.log('API response:', response);
          problemsToReview = response.data;
          console.log('Round 1: Found all Meta problems:', problemsToReview.length);
        } else {
          // Round 2+: Mix of "Need to Review" problems + random Meta problems
          console.log(`Round ${roundNumber}: Building mixed review set...`);
          
          // Get all Meta problems
          const allMetaResponse = await apiService.getProblems({ company: 'Meta', limit: 200 });
          const allMetaProblems = allMetaResponse.data;
          console.log('Found all Meta problems:', allMetaProblems.length);
          
          // Get problems that need review from previous round
          const previousRoundResults = JSON.parse(
            localStorage.getItem(`review_round_${roundNumber - 1}`) || '{}'
          );
          const needReviewProblems = allMetaProblems.filter(
            (p: Problem) => previousRoundResults[p.id]?.result === 'need_review'
          );
          console.log('Problems needing review from previous round:', needReviewProblems.length);
          
          if (needReviewProblems.length === 0) {
            // No problems need review, show message and redirect
            alert(`No problems need review from round ${roundNumber - 1}. Starting a new review session.`);
            navigate('/reviews?round=1');
            return;
          }
          
          // Calculate 2/3 need review, 1/3 random
          // Cap the session at reasonable size, but scale based on need review problems
          const maxSessionSize = Math.min(Math.max(needReviewProblems.length, 10), 30);
          const needReviewCount = Math.ceil(maxSessionSize * 2 / 3);
          const randomCount = maxSessionSize - needReviewCount;
          
          console.log(`Building review set: ${needReviewCount} need review + ${randomCount} random`);
          
          // Take first needReviewCount problems that need review
          const selectedNeedReview = needReviewProblems.slice(0, needReviewCount);
          
          // Get random problems (excluding ones that need review)
          const availableForRandom = allMetaProblems.filter(
            (p: Problem) => !needReviewProblems.some(nr => nr.id === p.id)
          );
          const shuffledRandom = [...availableForRandom].sort(() => Math.random() - 0.5);
          const selectedRandom = shuffledRandom.slice(0, randomCount);
          
          // Combine and shuffle the final set
          problemsToReview = [...selectedNeedReview, ...selectedRandom].sort(() => Math.random() - 0.5);
          console.log(`Round ${roundNumber}: Created mixed review set with ${problemsToReview.length} problems`);
        }

        if (problemsToReview.length === 0) {
          // No problems to review
          console.log('No problems to review, redirecting to home');
          alert(roundNumber === 1 ? 
            'No Meta problems found. Please check your data.' :
            `No problems available for review.`
          );
          navigate('/');
          return;
        }

        // Shuffle problems for variety
        const shuffled = [...problemsToReview].sort(() => Math.random() - 0.5);

        const newSession: ReviewSession = {
          sessionId: `review_${Date.now()}`,
          problems: shuffled,
          currentIndex: 0,
          startTime: Date.now(),
          reviewRound: roundNumber,
          reviewedProblems: {},
        };

        console.log('Session initialized with', shuffled.length, 'problems');
        setSession(newSession);
        setShowNotes(false);
        setUserNotes('');
      } catch (error) {
        console.error('Failed to initialize review session:', error);
        alert('Failed to load review session. Please try again.');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    initializeSession();
  }, [isAuthenticated, authLoading, navigate, roundNumber]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const finishSession = useCallback((completedSession: ReviewSession) => {
    // Save session results
    localStorage.setItem(
      `review_round_${completedSession.reviewRound}`,
      JSON.stringify(completedSession.reviewedProblems)
    );

    // Calculate stats
    const totalProblems = completedSession.problems.length;
    const remembered = Object.values(completedSession.reviewedProblems).filter(
      r => r.result === 'remember'
    ).length;
    const needReview = totalProblems - remembered;

    // Show results or navigate to next round
    navigate(`/reviews/results?round=${completedSession.reviewRound}&total=${totalProblems}&remembered=${remembered}&needReview=${needReview}`);
  }, [navigate]);

  const handleReviewResult = useCallback((result: 'remember' | 'need_review') => {
    if (!session || !currentProblem) return;

    const updatedSession = {
      ...session,
      reviewedProblems: {
        ...session.reviewedProblems,
        [currentProblem.id]: {
          result,
          notes: userNotes,
          timestamp: Date.now(),
        },
      },
    };

    setSession(updatedSession);

    // Move to next problem or finish session
    if (updatedSession.currentIndex < updatedSession.problems.length - 1) {
      setSession(prev => prev ? { ...prev, currentIndex: prev.currentIndex + 1 } : null);
      setShowNotes(false);
      setUserNotes('');
      resetVerificationState();
    } else {
      // Session complete
      finishSession(updatedSession);
    }
  }, [session, currentProblem, userNotes, finishSession]);

  const handleShowNotes = () => {
    setShowNotes(true);
  };

  const handleNextProblem = useCallback(() => {
    if (!session) return;

    if (session.currentIndex < session.problems.length - 1) {
      setSession(prev => prev ? { ...prev, currentIndex: prev.currentIndex + 1 } : null);
      setShowNotes(false);
      setUserNotes('');
      resetVerificationState();
    }
  }, [session]);

  const handlePreviousProblem = () => {
    if (!session) return;

    if (session.currentIndex > 0) {
      setSession(prev => prev ? { ...prev, currentIndex: prev.currentIndex - 1 } : null);
      setShowNotes(false);
      setUserNotes('');
      resetVerificationState();
    }
  };

  const resetVerificationState = () => {
    setExplanationInput('');
    setVerificationResult(null);
    setVerificationError('');
    setIsVerifying(false);
  };

  const handleVoiceRecordingReceived = useCallback((result: { transcription: string; audioBlob: Blob; mimeType: string }) => {
    if (!currentProblem) return;

    const newRecording: VoiceRecording = {
      id: `recording-${currentProblem.id}-${Date.now()}`,
      transcription: result.transcription,
      audioBlob: result.audioBlob,
      mimeType: result.mimeType,
      timestamp: Date.now()
    };
    
    // Auto-save to localStorage (without the blob for now)
    const recordingToSave = {
      id: newRecording.id,
      transcription: newRecording.transcription,
      mimeType: newRecording.mimeType,
      timestamp: newRecording.timestamp
    };
    localStorage.setItem(`voice_recording_${currentProblem.id}`, JSON.stringify(recordingToSave));
    
    // Update state
    setCurrentProblemVoiceRecording(newRecording);
    
    // Add transcription to explanation input
    const combinedText = [explanationInput, result.transcription].filter(Boolean).join(' ').trim();
    setExplanationInput(combinedText);
    
    console.log('Voice recording saved for problem:', currentProblem.id);
  }, [currentProblem, explanationInput]);

  const handleVoiceError = useCallback((error: string) => {
    setVerificationError(error);
  }, []);

  const clearVoiceRecording = useCallback(() => {
    if (!currentProblem) return;
    
    localStorage.removeItem(`voice_recording_${currentProblem.id}`);
    setCurrentProblemVoiceRecording(null);
    console.log('Voice recording cleared for problem:', currentProblem.id);
  }, [currentProblem]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _ensureLanguageAvailable = useCallback(async (problem: any) => {
    if (!isAuthenticated || !userProfile?.preferredLanguage || userProfile.preferredLanguage === 'python') {
      return problem; // Python is always available, or user not authenticated
    }

    // Check if user's preferred language is available
    if (problem.code.implementations[userProfile.preferredLanguage]) {
      return problem; // Language already available
    }

    // Generate language code on-demand
    console.log(`🔄 User prefers ${userProfile.preferredLanguage}, generating code...`);
    setIsGeneratingCode(true);
    
    try {
      const implementations = await apiService.generateLanguageCode(problem.id, userProfile.preferredLanguage);
      
      // Update the problem with the new implementations
      const updatedProblem = {
        ...problem,
        code: {
          ...problem.code,
          implementations: {
            ...problem.code.implementations,
            [userProfile.preferredLanguage]: implementations
          }
        }
      };
      
      console.log(`✅ Generated ${userProfile.preferredLanguage} code for problem`);
      return updatedProblem;
    } catch (error) {
      console.error(`Failed to generate ${userProfile.preferredLanguage} code:`, error);
      return problem; // Return original problem if generation fails
    } finally {
      setIsGeneratingCode(false);
    }
  }, [isAuthenticated, userProfile?.preferredLanguage]);

  const handleVerifyUnderstanding = async () => {
    if (!currentProblem || !explanationInput.trim()) {
      setVerificationError('Please provide your explanation to verify your understanding.');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      // If user hasn't accessed their notes yet, we'll compare against problem patterns and general knowledge
      // If they have accessed notes, we'll use their notes as reference
      const originalNotes = showNotes && userNotes.trim() 
        ? userNotes.trim() 
        : `Pattern: ${currentProblem.pattern.name}\nDescription: ${currentProblem.pattern.description}\nKey Points: ${currentProblem.pattern.keyPoints.join(', ')}`;

      const verifyRequest: VerifyReviewRequest = {
        problemId: currentProblem.id,
        originalNotes: originalNotes,
        textInput: explanationInput.trim(),
      };

      const result = await apiService.verifyReview(verifyRequest);
      setVerificationResult(result);
    } catch (error: any) {
      console.error('Verification error:', error);

      // Check if this is a usage limit error (402 Payment Required)
      const isUsageLimitError = error?.status === 402 ||
                                error?.message?.includes('usage-limit-exceeded') ||
                                error?.originalError?.status === 402;

      if (isUsageLimitError) {
        // Open paywall modal instead of showing generic error
        openPaywall('no-credits', 'review-verification');
      } else {
        // Show generic error for other errors
        setVerificationError(error.message || 'Failed to verify understanding. Please try again.');
      }
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCloseVerificationResult = () => {
    setVerificationResult(null);
  };

  // Add keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!showNotes) return;

      switch (e.key.toLowerCase()) {
        case 'r':
          if (e.ctrlKey || e.metaKey) return; // Don't interfere with browser shortcuts
          e.preventDefault();
          handleReviewResult('remember');
          break;
        case 't':
          if (e.ctrlKey || e.metaKey) return;
          e.preventDefault();
          handleReviewResult('need_review');
          break;
        case 'n':
          if (e.ctrlKey || e.metaKey) return;
          e.preventDefault();
          handleNextProblem();
          break;
        case 'escape':
          e.preventDefault();
          navigate('/');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showNotes, handleReviewResult, handleNextProblem, navigate]);

  if (authLoading || loading) {
    return (
      <div className="review-page-loading">
        <div className="loading-spinner"></div>
        <p>{authLoading ? 'Checking authentication...' : 'Loading review session...'}</p>
      </div>
    );
  }

  if (!session || !currentProblem) {
    return (
      <div className="review-page-error">
        <p>No problems available for review.</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Back to Home
        </button>
      </div>
    );
  }

  const progress = ((session.currentIndex + 1) / session.problems.length) * 100;
  const needsReviewCount = Object.values(session.reviewedProblems).filter(
    r => r.result === 'need_review'
  ).length;

  return (
    <div className="review-page">
      {/* Header */}
      <div className="review-header">
        <div className="session-info">
          <span className="session-timer">Session: {formatTime(sessionTime)}</span>
          <span className="problem-timer">Problem: {formatTime(problemTime)}</span>
        </div>
        <div className="progress-info">
          <span className="current-problem">
            {session.currentIndex + 1} / {session.problems.length}
          </span>
          <span className="needs-review">
            Need Review: {needsReviewCount}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      {/* Problem Card */}
      <div className="review-problem-card">
        {!showNotes ? (
          /* Problem View */
          <div className="problem-view">
            <div className="problem-header">
              <h2 className="problem-title">{currentProblem.title}</h2>
              <div className="problem-meta">
                <span className="difficulty-badge difficulty-{currentProblem.difficulty.toLowerCase()}">
                  {currentProblem.difficulty}
                </span>
                {currentProblem.leetcode_number && (
                  <span className="leetcode-number">LeetCode {currentProblem.leetcode_number}</span>
                )}
              </div>
            </div>

            <div className="problem-content">
              <div 
                className="problem-description"
                dangerouslySetInnerHTML={{ __html: currentProblem.description.problem }}
              />

              {currentProblem.description.examples.length > 0 && (
                <div className="problem-examples">
                  <h4>Examples:</h4>
                  {currentProblem.description.examples.map((example, idx) => (
                    <div key={idx} className="example">
                      <div className="example-input">
                        <strong>Input:</strong> <code>{example.input}</code>
                      </div>
                      <div className="example-output">
                        <strong>Output:</strong> <code>{example.output}</code>
                      </div>
                      {example.explanation && (
                        <div className="example-explanation">
                          <strong>Explanation:</strong> {example.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {currentProblem.description.constraints.length > 0 && (
                <div className="problem-constraints">
                  <h4>Constraints:</h4>
                  <ul>
                    {currentProblem.description.constraints.map((constraint, idx) => (
                      <li key={idx}>{constraint}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="explanation-section">
              <h3>🎯 Explain Your Approach</h3>
              <p>Before viewing your notes, explain how you would solve this problem using voice, text, or both. This will help verify your understanding.</p>
              
              <VoiceTextInput
                value={explanationInput}
                onChange={setExplanationInput}
                placeholder="Explain your approach to solving this problem using voice or text or both."
                rows={4}
                className="explanation-input"
                voiceRecording={currentProblemVoiceRecording}
                onVoiceRecordingReceived={handleVoiceRecordingReceived}
                onVoiceError={handleVoiceError}
                onClearVoiceRecording={clearVoiceRecording}
              />
              
              {verificationError && (
                <div className="verification-error">
                  ⚠️ {verificationError}
                </div>
              )}
            </div>

            {isGeneratingCode && (
              <div className="code-generation-status">
                <div className="spinner"></div>
                <span>Generating {userProfile?.preferredLanguage} code solutions...</span>
              </div>
            )}

            <div className="problem-actions">
              <button
                className="verify-understanding-btn"
                onClick={handleVerifyUnderstanding}
                disabled={isVerifying || !explanationInput.trim() || isGeneratingCode}
              >
                {isVerifying ? (
                  <>
                    <div className="spinner"></div>
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    🔍 Verify Understanding
                    <span className="shortcut">Check your explanation</span>
                  </>
                )}
              </button>
              
              <button
                className="btn btn-secondary"
                onClick={handleShowNotes}
              >
                📝 Show Notes & Patterns
              </button>
            </div>
          </div>
        ) : (
          /* Notes View */
          <div className="notes-view">
            <div className="problem-header">
              <h2 className="problem-title">{currentProblem.title}</h2>
              <div className="problem-meta">
                <span className="difficulty-badge difficulty-{currentProblem.difficulty.toLowerCase()}">
                  {currentProblem.difficulty}
                </span>
                {currentProblem.leetcode_number && (
                  <span className="leetcode-number">LeetCode {currentProblem.leetcode_number}</span>
                )}
              </div>
            </div>

            <div className="user-solution-section">
              <h3>💻 Your Solution</h3>
              {userSolution ? (
                <>
                  <div className="solution-type-badge">
                    {userSolution.solutionType === 'code' ? '🖥️ Code' : '📋 Algorithm'}
                  </div>
                  <div className="solution-content">
                    <pre className="solution-code">{userSolution.solutionCode}</pre>
                  </div>
                </>
              ) : (
                <div className="no-solution-message">
                  <p>📝 No solution saved yet for this problem.</p>
                  <p className="hint">This is a great opportunity to practice! Try to solve it and save your solution.</p>
                </div>
              )}
            </div>

            <div className="user-notes-section">
              <h3>📝 Your Solution Notes</h3>
              <textarea
                className="user-notes-input"
                placeholder="Add your notes about this problem..."
                value={userNotes}
                onChange={(e) => setUserNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div className="pattern-section">
              <h3>🎯 Pattern Recognition</h3>
              <div className="pattern-tags">
                {currentProblem.tags.map((tag, idx) => (
                  <span key={idx} className="pattern-tag">{tag}</span>
                ))}
              </div>
              <div className="pattern-details">
                <h4>{currentProblem.pattern.name}</h4>
                <p>{currentProblem.pattern.description}</p>
              </div>
            </div>

            <div className="review-actions">
              <button
                className="btn btn-success review-btn"
                onClick={() => handleReviewResult('remember')}
              >
                ✅ I Remember This! <span className="shortcut">Press R</span>
              </button>
              <button
                className="btn btn-warning review-btn"
                onClick={() => handleReviewResult('need_review')}
              >
                🔄 Need to Review <span className="shortcut">Press T</span>
              </button>
              <button
                className="btn btn-primary review-btn"
                onClick={handleNextProblem}
                disabled={session.currentIndex >= session.problems.length - 1}
              >
                ➡️ Next Problem <span className="shortcut">Press N</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="review-navigation">
        <button
          className="nav-btn"
          onClick={handlePreviousProblem}
          disabled={session.currentIndex === 0}
        >
          ← Previous
        </button>
        <button
          className="nav-btn"
          onClick={handleNextProblem}
          disabled={session.currentIndex >= session.problems.length - 1}
        >
          Next →
        </button>
      </div>

      {/* Verification Result Modal */}
      {verificationResult && (
        <ReviewVerificationResultComponent
          result={verificationResult}
          onClose={handleCloseVerificationResult}
        />
      )}
    </div>
  );
};